/**
 * NEW_UX effective-value brain — the pure state machine that decides whether the new
 * UX is on for a user and whether the announcement banner shows.
 *
 * Mirrors @dadohr/shared/services/newUx (kept locally) — the shared package publishes
 * only domainTypes/versions/locales to the frontend, not services/*, so FE code
 * reimplements shared service logic locally (see the same pattern for calculations.ts
 * in services/user). Keep this in sync with the shared module; the authoritative
 * unit-test truth table lives there.
 */

/**
 * Users created at/after this instant are "new" → NEW_UX is locked on with no opt-out.
 * TODO(ship): set to the actual prod deploy instant of the NEW_UX rollout. Mirror of
 * NEW_UX_LAUNCH_DATE_MS in @dadohr/shared/services/newUx.
 */
export const NEW_UX_LAUNCH_DATE_MS: number = Date.UTC(2026, 7, 25); // 2026-08-25 UTC

// TAG (Aspen Dental) org ids. Mirror of ORG_IDS.{prod.tag, dev.littleImprovements} in
// @dadohr/shared/services/orgIds (dev reuses littleImprovements as the TAG stand-in).
const TAG_ORG_ID_PROD = '8l3Yr1Ns5';
const TAG_ORG_ID_DEV = 'x_NUp47wZ';

export type NewUxDeployEnv = 'prod' | 'dev';

export const isTagOrg = (
  orgId: string | null | undefined,
  env: NewUxDeployEnv
): boolean => !!orgId && orgId === (env === 'prod' ? TAG_ORG_ID_PROD : TAG_ORG_ID_DEV);

export interface NewUxInputs {
  /** Persisted `newUxAcknowledged` from the user's admin doc. */
  acknowledged: boolean | null | undefined;
  /** Account creation time in epoch millis. Null/undefined ⇒ treated as existing. */
  createdAtMs: number | null | undefined;
  /** Whether the user's org is the TAG org (resolve via {@link isTagOrg}). */
  isTag: boolean;
  /** Session-scoped temporary opt-out for the current login. Never persisted. */
  sessionOff: boolean;
}

export interface NewUxState {
  /** Effective value: render the new UX when true, the full old UI when false. */
  enabled: boolean;
  /** Show the announcement banner (pending users only). */
  showBanner: boolean;
  /** New user — forced on, no toggle anywhere. */
  locked: boolean;
  /** The user has committed to on (banner permanently dismissed). */
  acknowledged: boolean;
  /** The user's org is the TAG org. */
  isTag: boolean;
}

/**
 * Effective NEW_UX state. Precedence:
 *   1. locked (new user)         → on
 *   2. sessionOff (temp opt-out) → off
 *   3. acknowledged (committed)  → on
 *   4. pending default           → on for everyone except TAG-org users
 */
export const computeNewUxState = (inputs: NewUxInputs): NewUxState => {
  const acknowledged = inputs.acknowledged === true;
  const locked =
    typeof inputs.createdAtMs === 'number' && inputs.createdAtMs >= NEW_UX_LAUNCH_DATE_MS;

  let enabled: boolean;
  if (locked) {
    enabled = true;
  } else if (inputs.sessionOff) {
    enabled = false;
  } else if (acknowledged) {
    enabled = true;
  } else {
    enabled = !inputs.isTag;
  }

  // Banner shows to any non-locked user not currently on the new UX they committed to:
  // pending users (announcement) AND committed users who switched to classic view (it
  // returns and stays until they turn the new view back on). Hidden only for new
  // (locked) users and committed users viewing the new UX.
  const showBanner = !locked && (!acknowledged || !enabled);

  return { enabled, showBanner, locked, acknowledged, isTag: inputs.isTag };
};
